import type { ReactNode } from "react";

import { SurfaceCard } from "../../features/shared/ui/surface/SurfaceCard";
import { MetricTile } from "../../features/shared/ui/MetricTile";

const titleRowClasses =
  "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between";
const titleTextClasses = "text-lg font-semibold text-slate-900 dark:text-slate-100";
const subtitleClasses = "text-sm text-slate-500 dark:text-slate-400";
const badgeRowClasses = "flex flex-wrap gap-2 text-xs uppercase";
const actionRowClasses =
  "flex flex-wrap justify-end gap-2 text-sm text-slate-600 dark:text-slate-400";
const metricsGridClasses = "grid gap-3 sm:grid-cols-2 lg:grid-cols-3";
const cardBaseClasses = "flex flex-col gap-4";

export type SummaryMetric = {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
};

type SummaryCardProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  metrics?: SummaryMetric[];
  children?: ReactNode;
  className?: string;
  tone?: Parameters<typeof SurfaceCard>[0]["tone"];
  link?: Parameters<typeof SurfaceCard>[0]["link"];
};

export function SummaryCard({
  title,
  subtitle,
  badges,
  actions,
  metrics,
  children,
  className,
  tone = "default",
  link,
}: SummaryCardProps) {
  return (
    <SurfaceCard
      tone={tone}
      link={link}
      className={[cardBaseClasses, className].filter(Boolean).join(" ")}
    >
      <div className={titleRowClasses}>
        <div className="flex flex-col">
          <div className={titleTextClasses}>{title}</div>
          {subtitle ? <p className={subtitleClasses}>{subtitle}</p> : null}
            {badges ? <div className={badgeRowClasses}>{badges}</div> : null}
        </div>
        <div className="flex flex-col text-right sm:items-end">

          {actions ? (
            <div
              className={actionRowClasses}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {actions}
            </div>
          ) : null}
        </div>
      </div>

      {(metrics?.length ?? 0) > 0 || children ? (
        <SurfaceCard.Body gap="base" textTone="muted">
          {(metrics?.length ?? 0) > 0 ? (
            <div className={metricsGridClasses}>
              {metrics?.map((metric) => (
                <MetricTile
                  key={metric.label}
                  icon={metric.icon}
                  label={metric.label}
                  value={metric.value}
                  hint={metric.hint}
                />
              ))}
            </div>
          ) : null}
          {children}
        </SurfaceCard.Body>
      ) : null}
    </SurfaceCard>
  );
}
